// Spedizioni e resi della bottega: tariffe reali dell'artigiano, tempi, imballaggio,
// regole di reso Rarifex e impegni aggiuntivi della bottega.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Gift, HeartHandshake, Package, RotateCcw, ShieldCheck, Truck } from 'lucide-react';
import { useShop } from '../ShopContext';
import { PageHeader, RarifexMark, Section } from '../ShopUi';
import { RETURN_RULES, customTimeLabel, describeShipping, dispatchLabel, loadShopShipping } from '../../lib/shopPolicies';

function Block({ icon: Icon, title, children, testId }) {
  const { style } = useShop();
  return (
    <div className={`p-6 bg-white border border-slate-200 ${style.panel}`} data-testid={testId}>
      <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Icon className="w-5 h-5 text-shop-primary" /> {title}</h2>
      <div className="mt-4 text-sm text-slate-600 space-y-3">{children}</div>
    </div>
  );
}

export default function ShopShipping() {
  const { crafter, name, branding, menuKeys, paths } = useShop();
  const policies = branding.policies || {};
  const [shipping, setShipping] = useState(null);

  useEffect(() => {
    let active = true;
    loadShopShipping(crafter.id).then((d) => active && setShipping(d));
    return () => { active = false; };
  }, [crafter.id]);

  const rates = describeShipping(shipping);
  const custom = menuKeys.includes('su_misura') ? customTimeLabel(policies.custom_time) : null;

  return (
    <div data-testid="shop-shipping">
      <PageHeader title="Spedizioni e resi" subtitle={`Come ti arrivano le creazioni di ${name}, in quanto tempo e cosa succede se qualcosa non va.`} />
      <Section className="!pt-2">
        <div className="grid md:grid-cols-2 gap-6">
          <Block icon={Truck} title="Spedizione" testId="shop-shipping-rates">
            {!shipping ? (
              <div className="h-16 rounded-lg bg-slate-100 animate-pulse" />
            ) : (
              <ul className="divide-y divide-slate-100 -my-2">
                {rates.map((r) => (
                  <li key={r.name} className="py-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{r.name}</p>
                      {r.days && <p className="text-xs text-slate-500 mt-0.5">Consegna in {r.days} · Italia</p>}
                      {r.free && <p className="text-xs text-shop-primary font-medium mt-0.5">{r.free}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-slate-900">{r.price}</p>
                      <p className="text-xs text-slate-400">{r.perItem ? 'per articolo' : 'per ordine'}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-slate-500">Ogni spedizione è tracciata: segui il pacco da «I miei ordini».</p>
          </Block>

          <Block icon={Clock} title="Tempi" testId="shop-shipping-times">
            <p>
              <strong className="text-slate-800">Creazioni disponibili:</strong>{' '}
              {policies.dispatch_days ? `partono ${dispatchLabel(policies.dispatch_days)} dall'ordine.` : 'partono appena pronte per la spedizione.'}
            </p>
            <p><strong className="text-slate-800">Su ordinazione:</strong> il tempo di realizzazione è indicato nella scheda di ogni creazione.</p>
            {custom && <p><strong className="text-slate-800">Su misura:</strong> di solito servono {custom}; i tempi esatti li trovi nella proposta.</p>}
          </Block>

          {(policies.packaging_note || policies.gift_wrap) && (
            <Block icon={Package} title="Imballaggio" testId="shop-shipping-packaging">
              {policies.packaging_note && <p className="whitespace-pre-line">{policies.packaging_note}</p>}
              {policies.gift_wrap && (
                <p className="flex items-start gap-2"><Gift className="w-4 h-4 mt-0.5 text-shop-primary shrink-0" /> Confezione regalo su richiesta: chiedila in chat dopo l'ordine.</p>
              )}
            </Block>
          )}

          {policies.returns_note && (
            <Block icon={HeartHandshake} title="Il mio impegno in più" testId="shop-shipping-promise">
              <p className="whitespace-pre-line">{policies.returns_note}</p>
            </Block>
          )}
        </div>
      </Section>

      <Section title="Resi e garanzie" className="!pt-0">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6" data-testid="shop-shipping-returns">
          {RETURN_RULES.map((r, i) => (
            <div key={r.title} className="flex gap-3">
              {i === 0 ? <RotateCcw className="w-5 h-5 text-shop-primary shrink-0 mt-0.5" /> : <ShieldCheck className="w-5 h-5 text-shop-primary shrink-0 mt-0.5" />}
              <div>
                <p className="font-medium text-slate-900">{r.title}</p>
                <p className="mt-1 text-sm text-slate-600">{r.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
          Ordini, pagamenti e assistenza sono gestiti da <RarifexMark className="text-slate-700" />
          · <Link to="/resi-rimborsi" className="underline underline-offset-4 hover:text-shop-primary">Condizioni complete</Link>
          {menuKeys.includes('social') && (
            <>· <Link to={paths.page('contatti')} className="underline underline-offset-4 hover:text-shop-primary">Hai una domanda? Scrivimi</Link></>
          )}
        </p>
      </Section>
    </div>
  );
}
